/**
 * Serverkonsolidierung.
 *
 * Der Dialog beantwortet die drei Fragen, die vor einem Umzug wirklich zählen:
 * Passt es? Wie lange dauert es? Was bringt es? Erst darunter steht der Knopf.
 *
 * Bewusst kein Assistent mit Schritten: Der Spieler soll Ziel und Quellen
 * nebeneinander sehen und die Auswirkung jeder einzelnen Auswahl sofort
 * ablesen können. Ein Server, dessen Last nicht vollständig mitkommt, wird
 * deshalb nicht ausgeblendet, sondern rot markiert – die Information "das geht
 * *fast*" ist die wichtigste im ganzen Vorgang.
 */
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import type { Server, ServerId } from '@hosting-sim/core';
import { consolidationSuggestions, planConsolidation, serverCapacity } from '@hosting-sim/core';
import { Badge, Button, Modal, Notice, Select } from './ui';
import { useGame } from '../store/game';
import { bytes, duration, mbps, money, num, pct, watt } from '../lib/format';

/** Knopf mit Dialog – überall dort einsetzbar, wo eine Serverliste steht. */
export function ConsolidateButton({ preselectTarget }: { preselectTarget?: ServerId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Zusammenlegen</Button>
      {open && <ConsolidationDialog preselectTarget={preselectTarget} onClose={() => setOpen(false)} />}
    </>
  );
}

function ConsolidationDialog({ preselectTarget, onClose }: { preselectTarget?: ServerId; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);

  const servers = useMemo(() => (world ? Object.values(world.servers) : []), [world]);

  /*
   * Als Ziel taugt nur, was Last aufnehmen kann: laufend, mit Betriebssystem,
   * nicht dediziert, nicht selbst zur Abgabe vorgemerkt. Die Liste ist nach
   * Arbeitsspeicher sortiert – der große Knoten steht oben, weil er in fast
   * allen Fällen gemeint ist.
   */
  const targets = useMemo(
    () =>
      servers
        .filter((s) => s.os !== null && s.role !== 'dedicated' && s.role !== 'unassigned' && !s.retirement)
        .filter((s) => s.state === 'online' || s.state === 'degraded')
        .sort((a, b) => b.ramGb - a.ramGb),
    [servers],
  );

  const [targetId, setTargetId] = useState<ServerId | ''>(() => preselectTarget ?? targets[0]?.id ?? '');
  const [selected, setSelected] = useState<Set<ServerId>>(new Set());
  const [harvest, setHarvest] = useState(true);
  const [retire, setRetire] = useState(true);
  const [busy, setBusy] = useState(false);

  const target = targetId ? world?.servers[targetId] : undefined;

  /** Nur Server desselben Standorts und derselben Rolle können abgeben. */
  const sources = useMemo(() => {
    if (!target) return [];
    return servers
      .filter((s) => s.id !== target.id)
      .filter((s) => s.datacenterId === target.datacenterId && s.role === target.role)
      .filter((s) => !s.retirement)
      .sort((a, b) => a.health - b.health);
  }, [servers, target]);

  const plan = useMemo(() => {
    if (!world || !target || selected.size === 0) return null;
    return planConsolidation(world, { targetServerId: target.id, sourceServerIds: [...selected] });
  }, [world, target, selected]);

  const suggestions = useMemo(() => (world ? consolidationSuggestions(world).slice(0, 3) : []), [world]);

  if (!world) return null;

  const toggle = (id: ServerId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const blockedBySource = new Map<string, number>();
  for (const entry of plan?.blocked ?? []) {
    blockedBySource.set(entry.sourceServerId, (blockedBySource.get(entry.sourceServerId) ?? 0) + 1);
  }

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title="Server zusammenlegen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!plan || !!plan.error || plan.moves.length === 0}
            onClick={async () => {
              if (!target) return;
              setBusy(true);
              try {
                const result = await run({
                  type: 'server/consolidate',
                  targetServerId: target.id,
                  sourceServerIds: [...selected],
                  retireDrained: retire,
                  harvestParts: harvest,
                });
                if (result.ok) onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            Umzug starten
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {targets.length === 0 ? (
          <Notice tone="warning" title="Kein geeigneter Zielserver">
            Zusammenlegen braucht einen laufenden Server mit Betriebssystem, der keine dedizierte Maschine ist.
          </Notice>
        ) : (
          <>
            {suggestions.length > 0 && selected.size === 0 && (
              <Notice tone="info" title="Das würde sich lohnen">
                <ul className="space-y-1 text-xs">
                  {suggestions.map((s) => (
                    <li key={s.targetServerId}>
                      <button
                        type="button"
                        className="underline"
                        onClick={() => {
                          setTargetId(s.targetServerId);
                          setSelected(new Set(s.sourceServerIds));
                        }}
                      >
                        {s.sourceServerIds.length} Server auf {world.servers[s.targetServerId]?.name}
                      </button>{' '}
                      — spart {money(s.plan.monthlySaving)}/Monat Strom, bringt {money(s.plan.proceeds)} Erlös,{' '}
                      {bytes(s.plan.transferGb)} zu übertragen ({duration(s.plan.durationTicks)})
                    </li>
                  ))}
                </ul>
              </Notice>
            )}

            <Select label="Zielserver" value={targetId} onChange={(e) => { setTargetId(e.target.value as ServerId); setSelected(new Set()); }}>
              {targets.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.cpuCores} Kerne, {bytes(s.ramGb)}, {world.datacenters[s.datacenterId]?.city}
                </option>
              ))}
            </Select>

            {target && (
              <div>
                <p className="mb-2 text-xs font-medium">
                  Diese Server abgeben{' '}
                  <span className="font-normal text-panel-500 dark:text-panel-400">
                    (gleiche Rolle, gleicher Standort — nur dort ist eine Live-Migration möglich)
                  </span>
                </p>

                {sources.length === 0 ? (
                  <p className="text-xs text-panel-500 dark:text-panel-400">
                    Am Standort {world.datacenters[target.datacenterId]?.city} gibt es keinen zweiten Server mit der Rolle „{target.role}“.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {sources.map((s) => (
                      <SourceRow
                        key={s.id}
                        server={s}
                        checked={selected.has(s.id)}
                        blockedCount={blockedBySource.get(s.id) ?? 0}
                        onToggle={() => toggle(s.id)}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )}

            {plan && <PlanSummary plan={plan} retire={retire} harvest={harvest} />}

            {plan && !plan.error && plan.drained.length > 0 && (
              <div className="rounded-lg border divider p-3">
                <label className="flex items-start gap-2 text-xs">
                  <input type="checkbox" checked={retire} onChange={(e) => setRetire(e.target.checked)} className="mt-0.5" />
                  <span>
                    <strong>Geleerte Server anschließend verkaufen.</strong> Sie nehmen ab sofort keine Neukunden mehr an und
                    gehen weg, sobald der letzte Dienst herunter ist — ohne dass jemand danebenstehen muss.
                  </span>
                </label>
                {retire && (
                  <label className="mt-2 flex items-start gap-2 text-xs">
                    <input type="checkbox" checked={harvest} onChange={(e) => setHarvest(e.target.checked)} className="mt-0.5" />
                    <span>
                      Bauteile ins Ersatzteillager übernehmen statt mitzuverkaufen. Bringt {money(plan.partsProceeds)} weniger,
                      lässt sich aber in andere Server einbauen.
                    </span>
                  </label>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/** Eine Zeile der Quellenauswahl – mit dem, was tatsächlich darauf liegt. */
function SourceRow({
  server,
  checked,
  blockedCount,
  onToggle,
}: {
  server: Server;
  checked: boolean;
  blockedCount: number;
  onToggle: () => void;
}) {
  const world = useGame((s) => s.world)!;
  const capacity = serverCapacity(world, server.id);
  const belegung = capacity.ramGbUsed / Math.max(1, capacity.ramGb);
  const dienste = server.vpsIds.length + server.contractIds.length;

  return (
    <li>
      <label
        className={clsx(
          'flex cursor-pointer items-center gap-3 rounded-lg border p-2 text-xs transition',
          checked ? 'border-brand-400 bg-brand-500/5' : 'divider hover:bg-panel-500/5',
        )}
      >
        <input type="checkbox" checked={checked} onChange={onToggle} />
        <span className="min-w-0 flex-1">
          <span className="font-medium">{server.name}</span>
          <span className="ml-2 text-panel-500 dark:text-panel-400">
            {server.cpuCores} Kerne · {bytes(server.ramGb)} · {dienste} {dienste === 1 ? 'Dienst' : 'Dienste'} · {pct(belegung, 0)} belegt
          </span>
        </span>
        <span className="shrink-0 tabular text-panel-500 dark:text-panel-400">{watt(server.idleWatt)} Leerlauf</span>
        {server.health < 0.5 && <Badge tone="warning">Zustand {(server.health * 100).toFixed(0)} %</Badge>}
        {checked && blockedCount > 0 && <Badge tone="danger">{blockedCount} passen nicht</Badge>}
      </label>
    </li>
  );
}

/** Was der Umzug kostet und bringt – die eigentliche Entscheidungsgrundlage. */
function PlanSummary({
  plan,
  retire,
  harvest,
}: {
  plan: NonNullable<ReturnType<typeof planConsolidation>>;
  retire: boolean;
  harvest: boolean;
}) {
  const world = useGame((s) => s.world)!;

  if (plan.error) {
    return (
      <Notice tone="warning" title="So geht es nicht">
        {plan.error}
      </Notice>
    );
  }

  const erloes = retire ? plan.proceeds + (harvest ? 0 : plan.partsProceeds) : 0;

  return (
    <div className="space-y-3 rounded-lg border divider p-3">
      <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <div>
          <p className="text-panel-500 dark:text-panel-400">Verlagerungen</p>
          <p className="font-medium">{num(plan.moves.length)}</p>
        </div>
        <div>
          {/*
            Die Dauer ist die Summe aller Übertragungen und nicht die längste:
            Sie teilen sich die Anbindung des Zielknotens. Diese eine Zahl ist
            der Unterschied zwischen "ein Klick" und "eine Nachtschicht" – sie
            steht deshalb hier und nicht im Kleingedruckten.
          */}
          <p className="text-panel-500 dark:text-panel-400">Dauer</p>
          <p className="font-medium">{duration(plan.durationTicks)}</p>
          <p className="text-2xs text-panel-500 dark:text-panel-400">
            {bytes(plan.transferGb)} über {mbps(plan.rateMbps)}
          </p>
        </div>
        <div>
          <p className="text-panel-500 dark:text-panel-400">Ziel danach belegt</p>
          <p className="font-medium">
            {pct(plan.targetLoadBefore, 0)} → {pct(plan.targetLoadAfter, 0)}
          </p>
        </div>
        <div>
          <p className="text-panel-500 dark:text-panel-400">Frei im Rack</p>
          <p className="font-medium">{plan.freedRackUnits} HE</p>
        </div>
      </div>

      {plan.interruptedContracts > 0 && (
        <Notice tone="warning" title={`${plan.interruptedContracts} Dienste sehen eine Unterbrechung`}>
          Ein Konto wird kopiert und umgeschaltet; dieser eine Moment kostet Verfügbarkeit und Zufriedenheit. VPS wandern
          dagegen live — solange ihr Speicherabgleich mit der Leitung mithält. Wer die Unterbrechung ganz vermeiden will, legt
          die betroffenen Verträge vorher auf einen Backend-Pool: Dann trägt der Loadbalancer weiter, während umgezogen wird.
        </Notice>
      )}

      {plan.partial.length > 0 && (
        <Notice tone="danger" title={`${plan.partial.length} Server werden nicht leer`}>
          <p>
            Auf ihnen bleibt etwas liegen, sie lassen sich also nicht abgeben. Entweder Ziel vergrößern oder diese Server aus
            der Auswahl nehmen:
          </p>
          <ul className="ml-4 mt-1 list-disc">
            {plan.blocked.slice(0, 5).map((entry, index) => (
              <li key={`${entry.sourceServerId}-${index}`}>
                {world.servers[entry.sourceServerId]?.name}: {entry.label} — {entry.reason}
              </li>
            ))}
            {plan.blocked.length > 5 && <li>… und {plan.blocked.length - 5} weitere</li>}
          </ul>
        </Notice>
      )}

      {plan.drained.length > 0 && (
        <Notice tone="success" title={`${plan.drained.length} Server werden vollständig frei`}>
          <p>
            {plan.drained.map((id) => world.servers[id]?.name).filter(Boolean).join(', ')}
          </p>
          <p className="mt-1">
            Das spart <strong>{watt(plan.freedWatt)}</strong> Dauerleistung und damit rund{' '}
            <strong>{money(plan.monthlySaving)} Stromkosten im Monat</strong>
            {retire ? <> — dazu einmalig {money(erloes)} Verkaufserlös.</> : <>. Der Verkauf ist abgewählt; sie bleiben stehen.</>}
          </p>
        </Notice>
      )}
    </div>
  );
}

/**
 * Hinweis auf Server, die bereits zur Abgabe vorgemerkt sind.
 *
 * Ohne diese Anzeige wäre die Vormerkung unsichtbar – und ein Server, der keine
 * Neukunden mehr annimmt, ohne dass jemand weiß warum, ist ein Fehlerbild und
 * kein Merkmal.
 */
export function RetirementNotice() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);

  const vorgemerkt = useMemo(() => (world ? Object.values(world.servers).filter((s) => s.retirement) : []), [world]);
  if (!world || vorgemerkt.length === 0) return null;

  return (
    <Notice tone="info" title={`${vorgemerkt.length} Server zur Abgabe vorgemerkt`}>
      <p className="text-xs">
        Sie nehmen keine neuen Kunden mehr an und werden verkauft, sobald der letzte Dienst herunter ist.
      </p>
      <ul className="mt-1 space-y-0.5 text-2xs">
        {vorgemerkt.map((server) => {
          const rest = server.vpsIds.length + server.contractIds.length;
          return (
            <li key={server.id}>
              <strong>{server.name}</strong> —{' '}
              {rest === 0 ? 'leer, geht im nächsten Tick weg' : `noch ${rest} ${rest === 1 ? 'Dienst' : 'Dienste'} darauf`}{' '}
              <button
                type="button"
                className="underline"
                onClick={() => void run({ type: 'server/cancelRetirement', serverId: server.id })}
              >
                behalten
              </button>
            </li>
          );
        })}
      </ul>
    </Notice>
  );
}
